/**
 * Block Kit messages (contracts blockKit.ts), drawn from core `renderKit`: header, section (text, two-column fields,
 * a button or image accessory), divider, context, image and actions (buttons). Read-only except buttons, which call
 * `onButton` (ChannelViewModel.pressButton: the app's interactivity URL; a button with a URL also opens it).
 * Also the "Only visible to you" row for ephemeral app answers (Slack's look: an eye line and Dismiss).
 */
import { BlockKit as BK, type KitContainer, type RichTextContext, type TimelineItem } from "@bundle/core";
import type { EphemeralMessage, KitBlock } from "@bundle/contracts";
import { Fragment, useMemo, useState } from "react";
import { useT } from "../i18n/I18nProvider.tsx";
import { Avatar, Divider, Icon, Image, InlineText, Pressable, Spinner, Stack, Text, type InlineRun } from "../primitives/index.ts";
import { AppBadge } from "./Badges.tsx";
import { KitElementView, isValueElement } from "./BlockKitElements.tsx";

type KitSpan = BK.KitSpan;
type KitButtonNode = BK.KitButtonNode;

export type BlockKitProps = {
  blocks: KitBlock[];
  ctx: RichTextContext;
  /** A button was pressed, with the blocks' current values (absent = elements are shown disabled). */
  onButton?: (blockId: string, button: KitButtonNode, values: BK.KitValues) => void;
  /**
   * A select, picker, checkbox group, radio group, overflow menu (or a dispatching input) changed on a message:
   * ChannelViewModel.changeElement. The message's values are kept here, starting from `initialKitValues`.
   */
  onElement?: (blockId: string, element: BK.KitValueNode | BK.KitOverflowNode, value: string[], values: BK.KitValues) => void;
  /** Controlled values (modals: AppModalViewModel.values) and their changes; then `onElement` isn't used. */
  values?: BK.KitValues;
  onValue?: (blockId: string, actionId: string, value: string[]) => void;
  /** Modals: an overflow choice. */
  onPick?: (blockId: string, menu: BK.KitOverflowNode, value: string) => void;
  /** Modals: Enter in a one-line text input. */
  onEnter?: (blockId: string, actionId: string) => void;
  /** Under input blocks, by block id. */
  errors?: Record<string, string>;
  /** The conversation it's shown in (conversations selects' default). */
  channelId?: string;
  /** Where the blocks are (a message, a modal view, an App Home tab): external selects ask the app through it. */
  container?: KitContainer;
  onUserPress?: (userId: string) => void;
  onChannelPress?: (channelId: string) => void;
  testID: string;
};

/** Spans → lines of runs (mrkdwn keeps line breaks) and ``` blocks. */
type Piece = { kind: "line"; runs: InlineRun[] } | { kind: "pre"; text: string };

function pieces(spans: KitSpan[], p: BlockKitProps): Piece[] {
  const out: Piece[] = [];
  let line: InlineRun[] = [];
  const endLine = () => {
    out.push({ kind: "line", runs: line.length === 0 ? [{ text: " " }] : line });
    line = [];
  };
  for (const s of spans) {
    if (s.pre === true) {
      if (line.length > 0) endLine();
      out.push({ kind: "pre", text: s.text });
      continue;
    }
    const parts = s.text.split("\n");
    parts.forEach((text, i) => {
      if (i > 0) endLine();
      if (text !== "") line.push(run(s, text, p));
    });
  }
  if (line.length > 0) endLine();
  return out;
}

function run(s: KitSpan, text: string, p: BlockKitProps): InlineRun {
  const r: InlineRun = { text };
  if (s.bold === true) r.bold = true;
  if (s.italic === true) r.italic = true;
  if (s.strike === true) r.strike = true;
  if (s.code === true) r.code = true;
  if (s.link !== undefined) r.link = s.link;
  const userId = s.userId;
  if (userId !== undefined) {
    const m = p.ctx.members[userId];
    r.text = m === undefined ? text : `@${m.displayName}`;
    r.chip = userId === p.ctx.meId ? "self" : m?.kind === "agent" ? "agent" : "user";
    const press = p.onUserPress;
    if (press !== undefined) r.onPress = () => press(userId);
  }
  const channelId = s.channelId;
  if (channelId !== undefined) {
    const c = p.ctx.channels[channelId];
    r.text = c === undefined ? (text.startsWith("#") && text !== `#${channelId}` ? text : `#${p.ctx.privateChannelLabel ?? "private channel"}`) : `#${c.name}`;
    r.chip = "channel";
    const press = p.onChannelPress;
    if (press !== undefined && c !== undefined) r.onPress = () => press(channelId);
  }
  if (s.broadcast !== undefined) r.chip = "broadcast";
  return r;
}

function Spans({ spans, p, variant = "body", color }: { spans: KitSpan[]; p: BlockKitProps; variant?: "body" | "footnote" | "caption"; color?: "text.secondary" }) {
  return (
    <Stack gap="0.5">
      {pieces(spans, p).map((x, i) =>
        x.kind === "pre" ? (
          <Stack key={i} padding="3" radius="md" background="bg.surface" border="border.subtle">
            <Text variant="footnote" mono selectable>
              {x.text}
            </Text>
          </Stack>
        ) : (
          <InlineText key={i} runs={x.runs} variant={variant} color={color} />
        ),
      )}
    </Stack>
  );
}

function RichText({ nodes, p }: { nodes: BK.KitRichNode[]; p: BlockKitProps }) {
  return (
    <Stack gap="1.5">
      {nodes.map((r, i) => {
        switch (r.kind) {
          case "paragraph":
            return <Spans key={i} spans={r.spans} p={p} />;
          case "preformatted":
            return (
              <Stack key={i} padding="3" radius="md" background="bg.surface" border="border.subtle">
                <Text variant="footnote" mono selectable>
                  {r.text}
                </Text>
              </Stack>
            );
          case "quote":
            return (
              <Stack key={i} paddingX="3" border="border.subtle" radius="sm">
                <Spans spans={r.spans} p={p} color="text.secondary" />
              </Stack>
            );
          case "list": {
            const list = (
              <Stack gap="0.5">
                {r.items.map((item, j) => (
                  <Stack key={j} direction="horizontal" align="start" gap="1.5">
                    <Text variant="body" color="text.secondary">
                      {r.ordered ? `${r.offset + j + 1}.` : r.indent % 2 === 1 ? "◦" : "•"}
                    </Text>
                    <Stack grow>
                      <Spans spans={item} p={p} />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            );
            // Nested lists sit further in (one step per indent level).
            let out = list;
            for (let k = 0; k < r.indent; k += 1) out = <Stack paddingX="3">{out}</Stack>;
            return <Stack key={i}>{out}</Stack>;
          }
        }
      })}
    </Stack>
  );
}

function pairs<T>(list: readonly T[]): (T | undefined)[][] {
  const out: (T | undefined)[][] = [];
  for (let i = 0; i < list.length; i += 2) out.push([list[i], list[i + 1]]);
  return out;
}

export function BlockKit(p: BlockKitProps) {
  const t = useT();
  const nodes = useMemo(() => BK.renderKit(p.blocks), [p.blocks]);
  const [own, setOwn] = useState<BK.KitValues>(() => BK.initialKitValues(nodes, p.channelId));
  const values = p.values ?? own;
  const enabled = p.onButton !== undefined || p.onValue !== undefined;
  const valueOf = (blockId: string, e: BK.KitElementNode) => (e.kind === "button" || e.kind === "overflow" ? [] : (values[blockId]?.[e.actionId] ?? e.initial));
  const change = (blockId: string, e: BK.KitValueNode, v: string[], dispatch: boolean) => {
    if (p.onValue !== undefined) {
      p.onValue(blockId, e.actionId, v);
      return;
    }
    const next = { ...values, [blockId]: { ...values[blockId], [e.actionId]: v } };
    setOwn(next);
    if (dispatch) p.onElement?.(blockId, e, v, next);
  };
  const element = (blockId: string, e: BK.KitElementNode, testID: string, input?: { label: string; dispatch: boolean }) => {
    const view = elementView(blockId, e, testID, input);
    // Outside input blocks, dropdowns are compact (Slack), not full-width fields.
    return input === undefined && ((e.kind === "select" && !e.multi) || e.kind === "timepicker") ? <Stack width="tab.maxWidth">{view}</Stack> : view;
  };
  const elementView = (blockId: string, e: BK.KitElementNode, testID: string, input?: { label: string; dispatch: boolean }) => (
    <KitElementView
      element={e}
      value={valueOf(blockId, e)}
      ctx={p.ctx}
      label={input?.label}
      blockId={blockId}
      container={p.container}
      error={input === undefined ? undefined : p.errors?.[blockId]}
      disabled={!enabled}
      onChange={(v) => {
        if (isValueElement(e)) change(blockId, e, v, input === undefined || input.dispatch);
      }}
      onPress={p.onButton === undefined ? undefined : (b) => p.onButton?.(blockId, b, values)}
      onPick={
        !enabled || e.kind !== "overflow"
          ? undefined
          : (v) => (p.onPick !== undefined ? p.onPick(blockId, e, v) : p.onElement?.(blockId, e, [v], values))
      }
      onEnter={p.onEnter === undefined ? undefined : () => p.onEnter?.(blockId, e.actionId)}
      testID={testID}
    />
  );
  return (
    <Stack gap="2" testID={p.testID}>
      {nodes.map((n, i) => {
        const id = `${p.testID}-${i}`;
        switch (n.kind) {
          case "header":
            return (
              <Text key={i} variant="headline" weight="bold">
                {n.text}
              </Text>
            );
          case "divider":
            return <Divider key={i} />;
          case "section":
            return (
              <Stack key={i} direction="horizontal" align="start" gap="3">
                <Stack grow gap="2">
                  {n.text === undefined ? null : <Spans spans={n.text} p={p} />}
                  {/* Fields in two columns, like Slack. */}
                  {pairs(n.fields).map((row, j) => (
                    <Stack key={j} direction="horizontal" align="start" gap="4">
                      {row.map((f, k) => (
                        <Stack key={k} grow>
                          {f === undefined ? null : <Spans spans={f} p={p} />}
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                </Stack>
                {n.accessory === undefined ? null : n.accessory.kind === "image" ? (
                  <Image src={n.accessory.url} alt={n.accessory.alt} width={72} height={72} radius="md" />
                ) : (
                  element(n.blockId, n.accessory, `${id}-accessory`)
                )}
              </Stack>
            );
          case "context":
            return (
              <Stack key={i} direction="horizontal" wrap align="center" gap="1.5">
                {n.elements.map((e, j) =>
                  e.kind === "image" ? (
                    <Image key={j} src={e.url} alt={e.alt} width={16} height={16} radius="sm" />
                  ) : (
                    <Spans key={j} spans={e.spans} p={p} variant="caption" color="text.secondary" />
                  ),
                )}
              </Stack>
            );
          case "image":
            return (
              <Stack key={i} gap="1">
                {n.title === undefined ? null : (
                  <Text variant="footnote" color="text.secondary">
                    {n.title}
                  </Text>
                )}
                <Image src={n.url} alt={n.alt} width={360} radius="md" fit="contain" />
              </Stack>
            );
          case "actions":
            return (
              <Stack key={i} direction="horizontal" wrap align="center" gap="2">
                {n.elements.map((e) => (
                  <Fragment key={e.actionId}>{element(n.blockId, e, `${id}-${e.kind === "button" ? "button" : "element"}-${e.actionId}`)}</Fragment>
                ))}
              </Stack>
            );
          case "input":
            return (
              <Stack key={i} gap="1.5" testID={`${id}-input`}>
                <Stack direction="horizontal" align="center" gap="1">
                  <Text variant="body" weight="bold">
                    {n.label}
                  </Text>
                  {n.optional ? (
                    <Text variant="body" color="text.secondary">
                      {t("appModal.optional")}
                    </Text>
                  ) : null}
                </Stack>
                {element(n.blockId, n.element, `${id}-element-${n.element.actionId}`, { label: n.label, dispatch: n.dispatch })}
                {/* Text inputs and selects show the error themselves; other elements get it here. */}
                {p.errors?.[n.blockId] !== undefined && n.element.kind !== "text_input" && !(n.element.kind === "select" && !n.element.multi) && n.element.kind !== "timepicker" ? (
                  <Text variant="caption" color="status.danger.fg" testID={`${id}-error`}>
                    {p.errors[n.blockId] ?? ""}
                  </Text>
                ) : null}
                {n.hint === undefined ? null : (
                  <Text variant="caption" color="text.secondary">
                    {n.hint}
                  </Text>
                )}
              </Stack>
            );
          case "rich_text":
            return <RichText key={i} nodes={n.elements} p={p} />;
        }
      })}
    </Stack>
  );
}

/**
 * Where each ephemeral message shows: after the newest timeline message created before it (else after the newest
 * one). → message item key → its ephemerals, oldest first. `items` are in timeline order (oldest first).
 */
export function anchorEphemerals(items: readonly TimelineItem[], ephemerals: readonly EphemeralMessage[]): Map<string, EphemeralMessage[]> {
  const out = new Map<string, EphemeralMessage[]>();
  const messages = items.filter((i): i is Extract<TimelineItem, { type: "message" }> => i.type === "message");
  const last = messages.at(-1);
  if (last === undefined) return out;
  for (const e of ephemerals) {
    let anchor = last;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]!.message.createdAt <= e.createdAt) {
        anchor = messages[i]!;
        break;
      }
    }
    out.set(anchor.key, [...(out.get(anchor.key) ?? []), e]);
  }
  return out;
}

/**
 * Where the newest ephemeral message sits: "" when it hangs off the last row (scroll to the end), else the key of the
 * row it follows (an older one), or undefined when there is none.
 */
export function newestEphemeralAnchor(items: readonly TimelineItem[], ephemerals: readonly EphemeralMessage[]): string | undefined {
  const newest = ephemerals.at(-1);
  if (newest === undefined) return undefined;
  const [key] = [...anchorEphemerals(items, [newest]).keys()];
  if (key === undefined) return undefined;
  return key === items.at(-1)?.key ? "" : key;
}

/** "Running /deploy…" above the composer while an app's slash command is on its way (Slack's pending state). */
export function CommandRunningRow({ command, testID }: { command: string; testID: string }) {
  const t = useT();
  return (
    <Stack direction="horizontal" align="center" gap="1.5" testID={testID}>
      <Spinner size="icon.sm" color="text.tertiary" accessibilityLabel={t("composer.appCommand.running", { command })} />
      <Text variant="caption" color="text.tertiary">
        {t("composer.appCommand.running", { command })}
      </Text>
    </Stack>
  );
}

export type EphemeralRowProps = {
  message: EphemeralMessage;
  ctx: RichTextContext;
  onDismiss: () => void;
  onButton?: (blockId: string, button: KitButtonNode, values: BK.KitValues) => void;
  onElement?: BlockKitProps["onElement"];
  onUserPress?: (userId: string) => void;
  onChannelPress?: (channelId: string) => void;
  testID: string;
};

/** An app's answer only I see: "Only visible to you" line, the app as author, its text or blocks, Dismiss. */
export function EphemeralRow(p: EphemeralRowProps) {
  const t = useT();
  const m = p.message;
  return (
    <Stack direction="horizontal" align="start" gap="2.5" paddingX="5" paddingY="1.5" background="bg.surface" testID={p.testID}>
      <Avatar name={m.app.name} src={m.app.iconUrl} size="avatar.message" square />
      <Stack grow gap="1">
        <Stack direction="horizontal" align="center" gap="1.5">
          <Icon name="eye" size="icon.sm" color="text.tertiary" />
          <Text variant="caption" color="text.tertiary">
            {t("message.ephemeral.onlyYou")}
          </Text>
        </Stack>
        <Stack direction="horizontal" align="center" gap="1.5">
          <Text variant="body" weight="bold">
            {m.app.name}
          </Text>
          <AppBadge />
        </Stack>
        {m.appBlocks !== undefined && m.appBlocks.length > 0 ? (
          <BlockKit blocks={m.appBlocks} ctx={p.ctx} container={{ messageId: m.id }} onButton={p.onButton} onElement={p.onElement} channelId={m.channelId} onUserPress={p.onUserPress} onChannelPress={p.onChannelPress} testID={`${p.testID}-blocks`} />
        ) : (
          <Spans spans={BK.mrkdwnSpans(m.text)} p={{ blocks: [], ctx: p.ctx, testID: p.testID, onUserPress: p.onUserPress, onChannelPress: p.onChannelPress }} />
        )}
        <Pressable onPress={p.onDismiss} testID={`${p.testID}-dismiss`} accessibilityLabel={t("message.ephemeral.dismiss")}>
          <Text variant="caption" color="text.link">
            {t("message.ephemeral.dismiss")}
          </Text>
        </Pressable>
      </Stack>
    </Stack>
  );
}
